import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { legalService } from '../services/legalService';
import { MarkdownContent } from './MarkdownContent';

interface LegalAcceptanceModalProps {
  tosVersion: string;
  privacyVersion: string;
  onAccept?: () => void;
  /** When true, shows view-only mode: no accept flow, only Close. Accept/Decline buttons are never rendered. */
  viewOnly?: boolean;
  /** Required when viewOnly is true. Called when user closes the modal. */
  onClose?: () => void;
}

const LegalAcceptanceModal: React.FC<LegalAcceptanceModalProps> = ({
  tosVersion,
  privacyVersion,
  onAccept,
  viewOnly = false,
  onClose,
}) => {
  const { logout } = useAuth();
  const [tosContent, setTosContent] = useState<string | null>(null);
  const [privacyContent, setPrivacyContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'tos' | 'privacy'>('tos');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tos, privacy] = await Promise.all([
          legalService.getContent('tos'),
          legalService.getContent('privacy'),
        ]);
        if (!cancelled) {
          setTosContent(tos.content);
          setPrivacyContent(privacy.content);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load documents');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleAccept = async () => {
    if (viewOnly || !onAccept || !agreed || submitting) return;
    setSubmitting(true);
    try {
      await legalService.accept(tosVersion, privacyVersion);
      onAccept();
    } catch (e: any) {
      setError(e?.message || 'Failed to record acceptance');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = () => {
    logout();
  };

  const content = activeTab === 'tos' ? tosContent : privacyContent;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-3xl max-h-[90vh] flex flex-col p-0">
        <div className="p-4 border-b border-base-300">
          <h2 className="font-bold text-xl">Terms of Service and Privacy Policy</h2>
          <p className="text-sm text-base-content/80 mt-1">
            {viewOnly
              ? 'View the current Terms of Service and Privacy Policy.'
              : 'You must read and accept these documents to continue using the LTS Portal.'}
          </p>
        </div>

        <div className="tabs tabs-lift px-4 pt-2 bg-base-200/50">
          <button
            type="button"
            className={`tab ${activeTab === 'tos' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('tos')}
          >
            Terms of Service
          </button>
          <button
            type="button"
            className={`tab ${activeTab === 'privacy' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('privacy')}
          >
            Privacy Policy
          </button>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto p-4 min-h-[300px] max-h-[50vh] prose prose-sm max-w-none"
        >
          {loading && (
            <div className="flex items-center justify-center py-12">
              <span className="loading loading-spinner loading-lg" />
            </div>
          )}
          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}
          {!loading && !error && content && <MarkdownContent markdown={content} />}
        </div>

        <div className="p-4 border-t border-base-300">
          {viewOnly ? (
            <div className="flex justify-end">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-primary mt-1"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span className="text-sm">
                  I have read and accept the Terms of Service and Privacy Policy. I understand that
                  continued use of the LTS Portal constitutes acceptance of these terms.
                </span>
              </label>

              <div className="flex justify-between mt-4">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={handleDecline}
                  disabled={submitting}
                >
                  Decline and Log Out
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!agreed || loading || submitting}
                  onClick={handleAccept}
                >
                  {submitting ? (
                    <>
                      <span className="loading loading-spinner loading-sm" />
                      Accepting...
                    </>
                  ) : (
                    'Accept and Continue'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LegalAcceptanceModal;
